import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { useGatewayUpdateUserFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/gateway-update-user.form-validation';
import { GatewayUpdateUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/gateway-update-user.model';
import { GatewayUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/outputs/gateway-user.model';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import * as globalCss from '@shared/styles/global.module.scss';

const UserUpdateFormStep1UserFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { control } = useFormContext<{
    userId: GatewayUserModel['id'];
    user: GatewayUpdateUserModel;
  }>();
  const { userLastName, userFirstName, userMobileNumber, userLandlineNumber } =
    useGatewayUpdateUserFormValidation();

  return (
    <Card direction="column" flex={1} gap={GapEnum.M}>
      <AccentTitle
        as={HeadingEnum.H2}
        icon="user-01"
        text={t('$.user-update.step-1.user.title')}
      />

      <Stack gap={GapEnum.M} flexWrap="wrap" className={globalCss.flexedItems}>
        <Controller
          name="user.firstName"
          control={control}
          rules={userFirstName}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-user.first-name')}
              placeholder={t('delmonicos-shared-sdk.outputs-user.first-name')}
              required={!!userFirstName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
        <Controller
          name="user.lastName"
          control={control}
          rules={userLastName}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-user.last-name')}
              placeholder={t('delmonicos-shared-sdk.outputs-user.last-name')}
              required={!!userLastName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
      </Stack>

      <Stack gap={GapEnum.M} flexWrap="wrap" className={globalCss.flexedItems}>
        <Controller
          name="user.mobileNumber"
          control={control}
          rules={userMobileNumber}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-user.mobile-number')}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-user.mobile-number',
              )}
              required={!!userMobileNumber.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
        <Controller
          name="user.landlineNumber"
          control={control}
          rules={userLandlineNumber}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-user.landline-number')}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-user.landline-number',
              )}
              required={!!userLandlineNumber.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
      </Stack>
    </Card>
  );
};

export const UserUpdateFormStep1Controller = (): React.JSX.Element => {
  return (
    <Stack gap={GapEnum.L} flex={1}>
      <UserUpdateFormStep1UserFragment />
    </Stack>
  );
};
